import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  ChevronDown, User, Lock, ExternalLink, LogOut, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Compact account menu — rendered in the dashboard top bar.
 * - Avatar + chevron toggles a small dropdown.
 * - Opens dialogs for "Mon profil" (edit name/phone) and "Mot de passe".
 * - "Voir le site public" opens "/" in a new tab.
 * - "Se déconnecter" clears the token + navigates home.
 */
export const UserMenu = ({ onLogout, homeHref = '/' }) => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const initials = (user?.first_name?.[0] || user?.email?.[0] || '?').toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 transition-colors"
        data-testid="admin-user-menu-button"
      >
        <span className="w-7 h-7 rounded-full bg-[#C9B896] text-[#1F2A4A] flex items-center justify-center text-xs font-semibold">
          {initials}
        </span>
        <span className="hidden md:inline text-xs text-white/85 font-medium max-w-[140px] truncate">
          {user?.first_name || user?.email}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-white/70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-[#1F2A4A]/10 overflow-hidden z-50"
          data-testid="admin-user-menu-dropdown"
        >
          <div className="px-4 py-3 border-b border-[#1F2A4A]/8 bg-[#FAF7F0]/50">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]">{user?.role}</p>
            <p className="text-sm font-medium text-[#1F2A4A] truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-[11px] text-[#4A5568] truncate">{user?.email}</p>
          </div>
          <div className="py-1">
            <MenuItem icon={User} label="Mon profil" onClick={() => { setOpen(false); setProfileOpen(true); }} testId="menu-profile" />
            <MenuItem icon={Lock} label="Changer le mot de passe" onClick={() => { setOpen(false); setPwdOpen(true); }} testId="menu-password" />
            <MenuItem icon={ExternalLink} label="Voir le site public" onClick={() => { window.open(homeHref, '_blank', 'noopener'); setOpen(false); }} testId="menu-public-site" />
          </div>
          <div className="py-1 border-t border-[#1F2A4A]/8">
            <MenuItem icon={LogOut} label="Se déconnecter" tone="danger" onClick={() => { setOpen(false); onLogout?.(); }} testId="menu-logout" />
          </div>
        </div>
      )}

      <ProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={user}
        onUpdated={(updated) => setUser?.(updated)}
      />
      <PasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, tone, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
      tone === 'danger'
        ? 'text-red-600 hover:bg-red-50'
        : 'text-[#1F2A4A] hover:bg-[#FAF7F0]'
    }`}
    data-testid={testId}
  >
    <Icon className="w-4 h-4 text-[#C9B896]" />
    {label}
  </button>
);

const ProfileDialog = ({ open, onClose, user, onUpdated }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
      });
    }
  }, [open, user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/auth/me`, form, { headers: authHeaders() });
      toast.success('Profil mis à jour');
      onUpdated?.(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1F2A4A] font-light">Mon profil</DialogTitle>
          <DialogDescription>Tes informations personnelles. L'email ne peut pas être modifié ici.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={user?.email || ''} disabled className="bg-[#FAF7F0]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Prénom</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="profile-first-name" />
            </div>
            <div>
              <Label className="text-xs">Nom</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="profile-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Téléphone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA]" data-testid="profile-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PasswordDialog = ({ open, onClose }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ current_password: '', new_password: '', confirm: '' }); }, [open]);

  const save = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) { toast.error('Le mot de passe doit faire au moins 6 caractères'); return; }
    if (form.new_password !== form.confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      }, { headers: authHeaders() });
      toast.success('Mot de passe modifié');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1F2A4A] font-light">Changer le mot de passe</DialogTitle>
          <DialogDescription>Choisis un mot de passe d'au moins 6 caractères.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs">Mot de passe actuel</Label>
            <Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required data-testid="pwd-current" />
          </div>
          <div>
            <Label className="text-xs">Nouveau mot de passe</Label>
            <Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required minLength={6} data-testid="pwd-new" />
          </div>
          <div>
            <Label className="text-xs">Confirmer</Label>
            <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={6} data-testid="pwd-confirm" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA]" data-testid="pwd-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Modifier
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserMenu;
